import React, { useEffect, useState } from 'react'
import './KaramalePolicy.css'
import Header3 from './components/Header3'
import Header from './components/Header'
import Header2 from './components/Header2'
import Header5 from './components/Header5'

const S = ({ children }) => <strong>{children}</strong>

const SECTIONS = [
  {
    id: 'validity',
    title: 'Ticket validity and entry',
    items: [
      <>
        Each ticket grants a <S>revocable license</S> for entry to the specified event.
      </>,
      <>
        Tickets are valid for <S>one-time use only</S> and are linked to a unique QR code and ticket
        number.
      </>,
      <>
        Entry requires a valid, scannable ticket. Failure to present one may result in denied
        access.
      </>,
      <>
        <S>Scanned tickets</S> are considered used and cannot be reused or transferred.
      </>,
    ],
  },
  {
    id: 'digital',
    title: 'Digital and actual tickets',
    items: [
      <>
        All <S>digital tickets</S> are delivered directly to the buyer's <S>Karamale account</S>.
      </>,
      <>
        Each digital ticket appears as a <S>QR code</S> showing key details like ticket type and
        number.
      </>,
      <>
        Buyers <S>tap to zoom</S> the QR code for venue scanning. No extra actions, printing, or
        downloads are required.
      </>,
      <>
        There are <S>no "actual tickets"</S> issued for digital purchases. The QR code <em>is</em>{' '}
        the ticket.
      </>,
      <>
        <S>Actual tickets</S> are physical print-outs, used only for offline or manually distributed
        events.
      </>,
    ],
  },
  {
    id: 'transfer',
    title: 'Ownership and transfer',
    items: [
      <>
        Tickets are <S>non-transferable</S> unless explicitly permitted by the event organiser.
      </>,
      <>Resale or promotional use requires written consent from Karamale.</>,
      <>Tickets bought through unauthorised platforms may be invalid and denied at entry.</>,
    ],
  },
  {
    id: 'security',
    title: 'Security and verification',
    items: [
      <>Secure QR validation prevents counterfeit tickets and fraud.</>,
      <>All entry attempts and organiser overrides are logged for transparency and auditing.</>,
      <>Tampering or repeated abuse may result in blacklisting or legal action.</>,
    ],
  },
  {
    id: 'changes',
    title: 'Event changes and notifications',
    items: [
      <>
        Event details are subject to change. Karamale will notify buyers by email, SMS, or dashboard
        alerts.
      </>,
      <>Buyers must keep their contact details up to date so notices reach them.</>,
    ],
  },
  {
    id: 'organiser',
    title: 'Organiser rights',
    items: [
      <>Organisers may deny entry for safety, compliance, or capacity reasons.</>,
      <>
        They may adjust the venue layout or enforce restrictions such as age limits and dress codes.
      </>,
      <>
        Overrides to ticketing rules are permitted under special circumstances and are logged
        securely.
      </>,
    ],
  },
]

const NAV = [
  ...SECTIONS.map(({ id, title }) => ({ id, title })),
  { id: 'refunds', title: 'Refunds' },
  { id: 'support', title: 'Support' },
]

function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0])
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length) {
          visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
          setActive(visible[0].target.id)
        }
      },
      { rootMargin: '-15% 0px -70% 0px' },
    )
    ids.forEach((id) => {
      const el = document.getElementById(id)
      if (el) obs.observe(el)
    })
    return () => obs.disconnect()
  }, [ids])
  return active
}

function KaramalePolicy() {
  useEffect(() => {
    document.title = 'Karamale Ticketing Policy'
  }, [])

  const active = useActiveSection(NAV.map((n) => n.id))

  return (
    <>
      {localStorage.getItem('customername') !== null ? (
        <Header2 />
      ) : localStorage.getItem('businessname') !== null ? (
        <Header3 />
      ) : localStorage.getItem('partner') !== null ? (
        <Header5 />
      ) : (
        <Header />
      )}

      <main className="kp">
        <div className="kp-wrap">
          <header className="kp-hero">
            <h1>Karamale Ticketing Policy</h1>
            <p>How tickets work, who can use them, and what happens at the door.</p>
          </header>

          <div className="kp-body">
            <nav className="kp-nav" aria-label="Policy sections">
              <ul>
                {NAV.map(({ id, title }) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      className={active === id ? 'is-active' : ''}
                      aria-current={active === id ? 'true' : undefined}
                    >
                      {title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <article className="kp-content">
              {SECTIONS.map(({ id, title, items }, i) => (
                <section key={id} id={id} className="kp-section">
                  <h2>
                    <span className="kp-num">{i + 1}.</span>
                    {title}
                  </h2>
                  <ul>
                    {items.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                </section>
              ))}

              <section id="refunds" className="kp-section">
                <h2>
                  <span className="kp-num">7.</span>Refunds
                </h2>
                <p>
                  Refund eligibility, fees, and timing are covered in our{' '}
                  <a href="/refund-policy">Refund Policy</a>.
                </p>
              </section>

              <section id="support" className="kp-section kp-support">
                <h2>
                  <span className="kp-num">8.</span>Support
                </h2>
                <p>
                  Trouble accessing your ticket, entering the event, or managing your account? Reach
                  out through your dashboard or email us.
                </p>
                <a className="kp-btn" href="mailto:support@karamale.com">
                  support@karamale.com
                </a>
                <p className="kp-note">
                  Include your ticket number and event name for faster help. We respond within{' '}
                  <strong>48 hours</strong>.
                </p>
              </section>
            </article>
          </div>
        </div>
      </main>
    </>
  )
}

export default KaramalePolicy
